import {
  EmailIntakeSenderMatchType,
  type ReceivedEmailRejectionReason,
  type ReceivedEmailActionKind,
  type ReceivedEmailAttention,
  type ReceivedEmailStatus,
} from "generated-graphql/graphql";

/** The dialog's editable fields, shared by the tab (which opens it) and the dialog. */
export type FacilityHint = {
  id: string;
  name: string;
  city?: string | null;
  state?: string | null;
};

export type SenderDialogValues = {
  /** Whether the entry accepts mail. Undefined on a new entry, which defaults on. */
  enabled?: boolean;
  /** Which organization's allowlist. Null when adding and not yet chosen. */
  organization?: { id: string; name: string } | null;
  id?: string;
  matchType: EmailIntakeSenderMatchType;
  value: string;
  includeSubdomains: boolean;
  label: string;
  facility?: FacilityHint | null;
};

/** A blank address-level entry — the shape we want staff to reach for by default. */
export function emptySenderValues(): SenderDialogValues {
  return {
    matchType: EmailIntakeSenderMatchType.Address,
    value: "",
    includeSubdomains: false,
    label: "",
    facility: null,
  };
}

/** A row as the tab and its table read it. */
export type SenderRow = {
  id: string;
  matchType: EmailIntakeSenderMatchType;
  value: string;
  includeSubdomains: boolean;
  enabled: boolean;
  label?: string | null;
  facility?: FacilityHint | null;
  lastReceivedAt?: string | null;
  documentCount?: number;
  createdAt?: string | null;
  createdBy: string;
  updatedAt?: string | null;
  updatedBy: string;
};

/** An inbound email as the two inbound tables read it. */
export type InboundEmailRow = {
  id: string;
  receivedAt: string;
  fromAddress?: string | null;
  subject: string;
  status: ReceivedEmailStatus;
  /** Null when resolution could not name an organization. A state, not missing data. */
  tenant?: { id: string; name: string } | null;
  rejectionReason?: ReceivedEmailRejectionReason | null;
  attachmentsProcessedCount?: number | null;
  attachmentsTotalCount?: number | null;
  /** Whether anyone still owes this email something. Derived by the API. */
  attention: ReceivedEmailAttention;
  /**
   * Every action, whether it is available, and why not. Computed by the API from the
   * same rules the lambda re-checks, so the grid and the detail cannot disagree.
   */
  actions: ReadonlyArray<{
    kind: ReceivedEmailActionKind;
    available: boolean;
    blockedReason?: string | null;
  }>;
  /** A staff member's "needs no action", layered over the status. */
  dismissedAt?: string | null;
  dismissedBy?: string | null;
};

/** Prefills the dialog to allowlist a specific address for this tenant. */
export function senderValuesForAddress(fromAddress: string): SenderDialogValues {
  return {
    matchType: EmailIntakeSenderMatchType.Address,
    value: fromAddress,
    includeSubdomains: false,
    label: "",
    facility: null,
  };
}
